import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";

const normalizeImageFit = (fit) => {
  const value = String(fit || "cover").toLowerCase();
  if (["contain", "contain_width", "contain_height"].includes(value)) return value;
  return "cover";
};

const normalizeImageSize = (size) => {
  const value = String(size || "medium").toLowerCase();
  if (["small", "medium", "large", "xlarge"].includes(value)) return value;
  return "medium";
};

const imageSizeContainerClass = (size) => {
  const normalized = normalizeImageSize(size);
  if (normalized === "small") return "min-h-[160px]";
  if (normalized === "large") return "min-h-[300px]";
  if (normalized === "xlarge") return "min-h-[380px]";
  return "min-h-[220px]";
};

const imageFitClass = (fit) => {
  const normalized = normalizeImageFit(fit);
  if (normalized === "contain") return "object-contain bg-slate-100";
  if (normalized === "contain_width") return "object-contain object-top bg-slate-100";
  if (normalized === "contain_height") return "object-contain object-center bg-slate-100";
  return "object-cover";
};

const parseLayoutConfig = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_err) {
      return {};
    }
  }
  return {};
};

const PageSectionsRenderer = ({
  pageKey,
  className = "",
  title = "",
  excludeKeys = [],
  excludeKeyPrefixes = [],
}) => {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await API.fetchAboutSections(pageKey);
        if (!mounted) return;
        setSections(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!mounted) return;
        setSections([]);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [pageKey]);

  const activeSections = useMemo(() => {
    const keySet = new Set(
      (Array.isArray(excludeKeys) ? excludeKeys : [])
        .map((k) => String(k || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const prefixes = (Array.isArray(excludeKeyPrefixes) ? excludeKeyPrefixes : [])
      .map((k) => String(k || "").trim().toLowerCase())
      .filter(Boolean);

    return sections
      .filter((s) => s?.is_active !== false)
      .filter((s) => {
        const sectionKey = String(s?.section_key || "").trim().toLowerCase();
        if (!sectionKey) return true;
        if (keySet.has(sectionKey)) return false;
        if (prefixes.some((prefix) => sectionKey.startsWith(prefix))) return false;
        return true;
      })
      .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0));
  }, [sections, excludeKeys, excludeKeyPrefixes]);

  if (!activeSections.length) return null;

  return (
    <section className={`py-16 bg-white ${className}`.trim()}>
      <div className="max-w-screen-xl mx-auto px-6">
        {title ? (
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">{title}</h2>
          </div>
        ) : null}
        <div className="space-y-8">
          {activeSections.map((section, idx) => {
            const cfg = parseLayoutConfig(section.layout_config);
            const fitClass = imageFitClass(cfg.image_fit);
            const sizeClass = imageSizeContainerClass(cfg.image_size);

            return (
              <article
                key={section.id || `${pageKey}-${idx}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center bg-slate-50 rounded-2xl p-6 md:p-8 ${
                  idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  {section.subheading ? (
                    <p className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
                      {section.subheading}
                    </p>
                  ) : null}
                  <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
                    {section.heading}
                  </h3>
                  {section.body ? (
                    <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-line">
                      {section.body}
                    </p>
                  ) : null}
                  {section.cta_label && section.cta_link ? (
                    <a
                      href={section.cta_link}
                      className="inline-block mt-5 px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                    >
                      {section.cta_label}
                    </a>
                  ) : null}
                </div>
                <div className={`rounded-xl overflow-hidden shadow bg-slate-200 ${sizeClass}`}>
                  <img
                    src={resolveAssetUrl(section.image_path || "", {
                      folder: "page-sections",
                      defaultPath: "https://placehold.co/1200x700?text=Section+Image",
                    })}
                    alt={section.heading || "section"}
                    className={`w-full h-full ${fitClass}`}
                  />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

PageSectionsRenderer.propTypes = {
  pageKey: PropTypes.string.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  excludeKeys: PropTypes.arrayOf(PropTypes.string),
  excludeKeyPrefixes: PropTypes.arrayOf(PropTypes.string),
};

export default PageSectionsRenderer;
